import { withLDProvider, useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from 'react';
import Login from './components/auth/Login';
import Loader from './components/Loader';
import RestaurantsRoute from './pages/Restaurants/RestaurantsRoute';
import AdvancedSearch from './components/AdvancedSearch/AdvancedSearch';
import RestaurantDetails from './pages/Restaurants/RestaurantDetails';
import RestaurantEdit from './pages/Restaurants/RestaurantEdit';
import EquipmentDetails from './pages/Equipment/EquipmentDetails';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
} from 'react-router-dom';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import NavBar from './components/NavBar/NavBar';
import { Container, IconButton, Snackbar } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ThemeProvider as MUIThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider } from 'styled-components';
import { snackbarHideMessage } from './redux/actions/snackbarActions';
import { ConfirmProvider } from 'material-ui-confirm';
import './App.css';
import { actionListener } from './utils/helpers';
import store from './redux/store';
import {
  GET_AVAILABLE_LANGUAGES,
  SET_SELECTED_LANGUAGE,
  TOGGLE_PREFERENCES_MODAL,
} from './redux/action-types/app-types';
import PreferencesDialog from './components/Preferences/PreferencesDialog';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { OktaAuth } from '@okta/okta-auth-js';
import { browserHardRefresh } from './utils/utils';
import MassUploadsHistory from './pages/MassUploads/MassUploadsHistory';
import useDatadogRum from './hooks/useDataDogRUM';
import UnderMaintenance, { getUnderMaintenanceProps } from "./pages/errors/maintenance/UnderMaintenance";
import useJiraIssueCollector from "./hooks/useJiraIssueCollector";

const oidc = window['oidc'];
const oktaAuth = new OktaAuth(oidc);

const HasAccessToRouter = () => {
  const history = useHistory();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.push(originalUri);
  };
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <NavBar />
      <Switch>
        <Route exact path="/login" history={history} component={Login} />
        <Route path="/implicit/callback" component={LoginCallback} />
        <SecureRoute path="/advancedSearch" component={AdvancedSearch} />
        <SecureRoute path="/equipments/:id" component={EquipmentDetails} />
        <SecureRoute path="/massUploadHistory" component={MassUploadsHistory} />
        <SecureRoute path="/restaurants/:id/edit" component={RestaurantEdit} />
        <SecureRoute
          path="/restaurants/:id/:tab"
          component={RestaurantDetails}
        />
        <SecureRoute path="/restaurants/:id" component={RestaurantDetails} />
        <SecureRoute path="/restaurants" component={RestaurantsRoute} />
        <SecureRoute
          path="/:option/:restaurantId"
          component={RestaurantsRoute}
        />
        <SecureRoute path="/:option" component={RestaurantsRoute} />
        <SecureRoute path="/" component={RestaurantsRoute} />
      </Switch>
    </Security>
  );
};

const App = () => {
  const selectedTheme = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedTheme,
  );
  const snackbarShow = useSelector(
    (state: RootStateOrAny) => state.SnackbarReducer.show,
  );
  const snackbarMessage = useSelector(
    (state: RootStateOrAny) => state.SnackbarReducer.message,
  );
  const dispatch: any = useDispatch();
  const handleCloseSnackbar = () => {
    dispatch(snackbarHideMessage());
  };

  useJiraIssueCollector()

  const { t, i18n } = useTranslation();
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const { userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );

  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) return;

    const identifyInLDFromOkta = async () => {
      const userInfo = await oktaAuth.token.getUserInfo();
      const { sAMAccountName, preferred_username } = userInfo;
      await ldClient.waitForInitialization();
      const key = (sAMAccountName || '').toUpperCase();
      await ldClient.identify({ key, name: preferred_username });
    }

    identifyInLDFromOkta();
  }, [ldClient]);

  useEffect(() => {
    browserHardRefresh();
  }, []);

  useDatadogRum();
  useEffect(() => {
    const unsubscribeOpenPreferencesModal = actionListener(
      store,
      TOGGLE_PREFERENCES_MODAL,
      function (action) {
        setShowPreferencesModal(action.payload);
      },
    );

    return () => {
      unsubscribeOpenPreferencesModal();
    };
  }, [showPreferencesModal]);

  useEffect(() => {
    const unsubscribeGetLanguages = actionListener(
      store,
      GET_AVAILABLE_LANGUAGES,
      async function (action) {
        const selectedBrand = action.payload.brand;
        const selectedLanguage = localStorage.getItem('selectedLanguage');
        const languageFromFrum =
          userInfo.data?.payload?.preferences?.language?.code;

        if (!selectedBrand) {
          return;
        }

        if (languageFromFrum) {
          i18n.changeLanguage(languageFromFrum.toLowerCase());
        }

        if (selectedLanguage) {
          i18n.changeLanguage(selectedLanguage);
        } else {
          localStorage.setItem(
            'selectedLanguage',
            languageFromFrum?.toLowerCase() || 'eng',
          );
        }
        axios.interceptors.request.use(async config => {
          const selectedLanguage = localStorage.getItem('selectedLanguage');
          config.headers['selected-language'] =
            selectedLanguage || languageFromFrum.toLowerCase() || 'eng';
          return config;
        });
      },
    );
    const unsucribeChangeSelectedLanguage = actionListener(
      store,
      SET_SELECTED_LANGUAGE,
      async function (action) {
        const newLanguage = action.payload;
        i18n.changeLanguage(newLanguage);
        axios.interceptors.request.use(async config => {
          config.headers['selected-language'] = newLanguage;
          return config;
        });
      },
    );
    return () => {
      unsubscribeGetLanguages();
      unsucribeChangeSelectedLanguage();
    };
  });
  const { ramWarningBarToggle, ramMaintenance } = useFlags();
  return (
    <div className="App">
      <Loader>
        <Router>
          <MUIThemeProvider theme={selectedTheme}>
            <ThemeProvider theme={selectedTheme}>
              { ramMaintenance?.enabled ? (
                <UnderMaintenance
                  title={ramMaintenance?.title}
                  firstLine={ramMaintenance?.firstLine}
                  secondLine={ramMaintenance?.secondLine}
                  thirdLine={ramMaintenance?.thirdLine}
                />
              ) : (
                <ConfirmProvider>
                  <Container
                    maxWidth="lg"
                    style={{ paddingTop: ramWarningBarToggle ? 150 : 100, height: '100%' }}
                  >
                    <HasAccessToRouter />
                  </Container>
                </ConfirmProvider>
              ) }
            </ThemeProvider>
          </MUIThemeProvider>
        </Router>
      </Loader>
      <PreferencesDialog isOpen={showPreferencesModal} />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={snackbarShow}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={t(snackbarMessage)}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
};

export default withLDProvider({
  clientSideID: process.env.REACT_APP_LAUNCHDARKLY_ID!,
})(App);
